/**
 * Plan Started Trigger Component
 *
 * Trigger: "started a plan"
 * Shows: multi-select plans (plan-started variant for Assign Assessment)
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { Chip } from 'src/components/Chip';
import { TextField } from 'src/components/TextField';
import { fetchWorkoutPlanOptions } from 'src/models/workout-plan/workout-plan';

import { TriggerComponentProps } from '../automationTriggers';

interface PlanOption {
  label: string;
  value: string;
}

const getSelectedPlanIds = (
  reminderTypeId: number | string | (number | string)[] | null | undefined
): string[] => {
  if (!reminderTypeId) return [];
  if (Array.isArray(reminderTypeId)) {
    return reminderTypeId.map((id) => String(id));
  }
  return [String(reminderTypeId)];
};

export const PlanStartedTrigger: FC<TriggerComponentProps> = ({
  automation,
  index,
  readOnly,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  const { data: plans = [] } = useQuery({
    queryKey: ['workout-plans', 'options'],
    queryFn: () => fetchWorkoutPlanOptions(),
    staleTime: 60000,
  });

  const planOptions: PlanOption[] = plans.map((plan) => ({
    value: String(plan.id),
    label: plan.name,
  }));

  const selectedPlanIds = getSelectedPlanIds(automation.reminderTypeId);
  const selectedPlans = planOptions.filter((plan) =>
    selectedPlanIds.includes(plan.value)
  );

  if (readOnly) {
    if (selectedPlans.length > 0) {
      return (
        <span>
          {t('automations.triggers.startedPlanNamed', {
            plan: selectedPlans.map((plan) => plan.label).join(', '),
          })}
        </span>
      );
    }

    return <span>{t('automations.triggers.startedPlan')}</span>;
  }

  return (
    <Box sx={{ maxWidth: 400 }}>
      <Autocomplete<PlanOption, true>
        multiple
        options={planOptions}
        getOptionLabel={(option) => option.label}
        value={selectedPlans}
        onChange={(_, newValue) => {
          setFieldValue(
            `automations.${index}.reminderTypeId`,
            newValue.map((plan) => parseInt(plan.value, 10))
          );
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, optionIndex) => (
            <Chip
              size="small"
              label={option.label}
              {...getTagProps({ index: optionIndex })}
              key={option.value}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('automations.triggerLabels.plan')}
            placeholder={t('automations.triggerLabels.selectPlan')}
            size="small"
          />
        )}
        isOptionEqualToValue={(option, value) => option.value === value.value}
      />
    </Box>
  );
};
